// revenue.js — THE answer to "what is this job worth, and where does its money sit?"
//
// Born from the 2026-08-25 money audit (CL1/JM6/JM8/JM9/JM14/JM15): three
// incompatible accounting models ran at once — Insights double-counted booked
// change orders, the Dashboard/JobsList hid unbooked ones, and the job card
// omitted them entirely. Every money surface now goes through this module; no
// surface recomputes revenue inline.
//
// The model (Ivin, 2026-08-25 — settled):
//   1. ROLLUP — a change order is part of its parent project. A $10k job with a
//      $2k CO reads as ONE $12k project everywhere. The CO keeps its own quote
//      (and, once booked, its own child job) for paperwork and scheduling, but
//      is never separate revenue.
//   2. COs BILL INDEPENDENTLY — so one project's money can span AR tiers:
//      parent paid while the CO is still projected. A single scalar cannot say
//      that, hence revenue PARTS.
//
// Contract: pure functions of plain data. No React, no I/O.

import { resolveQuoteRevenue } from "../../../lib/builderHelpers.js";
import { isProBono } from "../../../lib/proBono.js";

// AR tier off billing milestones (Ivin, 2026-07-10): every live job is an
// accepted quote, so it is AR from day one — the tier is WHERE in the pipeline
// it sits. paid = collected; outstanding = invoiced, awaiting payment;
// projected = booked, not yet invoiced.
export const jobTier = (j) => (j?.paid ? "paid" : j?.invoiced ? "outstanding" : "projected");

// The counterpart to insights.js's isSupplementalQuote() guard, on the job
// side: bookJob stamps changeOrderOf on a CO quote's child job.
export const isChangeOrderJob = (j) => !!j?.changeOrderOf;

// Mirrors jobHasRevenueSyncGuard in DataContext.jsx (kept there un-touched while
// the contact-ingest slice is mid-flight in that file): the sync refuses to move
// grossRevenue once ANY billing marker exists — un-invoicing clears the flag but
// not invoiceDate, so the date alone still blocks. Any surface that warns about
// quote/revenue divergence must use this same predicate or it will hide exactly
// the divergences the guard creates (JM8 / gpt F4).
export const jobRevenueSyncLocked = (j) => !!(j?.invoiced || j?.paid || j?.invoiceDate || j?.paidDate);

const isLiveDoc = (x) => x && !x.deletedAt;

// True when this CO child's money is attributed to a live parent — meaning the
// row must be SKIPPED by every revenue iteration. An orphan (parent
// soft-deleted or missing) returns false and is counted once, on itself,
// rather than silently vanishing.
//
// Corrupt cycles (A→B→A) get one deterministic root — the smallest id in the
// cycle — so the whole component can never filter itself out of existence
// (gpt F6); jobRevenueParts' visited set then counts each member once.
export function isRolledUpChangeOrderJob(job, jobs) {
  if (!isChangeOrderJob(job)) return false;
  const byId = new Map((jobs || []).filter(isLiveDoc).map((j) => [j.id, j]));
  if (!byId.has(job.changeOrderOf)) return false; // orphan — counts on itself
  const chain = [job.id];
  const index = new Map([[job.id, 0]]);
  let cur = byId.get(job.changeOrderOf);
  while (cur) {
    if (index.has(cur.id)) {
      const cycle = chain.slice(index.get(cur.id));
      if (!cycle.includes(job.id)) return true; // hangs off a cycle — still has a live parent
      return job.id !== [...cycle].sort()[0];   // exactly one root per cycle
    }
    index.set(cur.id, chain.length);
    chain.push(cur.id);
    cur = cur.changeOrderOf ? byId.get(cur.changeOrderOf) : null;
  }
  return true; // parent chain is live and acyclic — this row rolls up
}

// The parts of one project's revenue:
//   { base: {amount, tier}, changeOrders: [{quoteId, jobId|null, amount, tier}], total }
//
// Attribution rules (each CO counted from exactly ONE place):
//   - A BOOKED CO contributes its child job's grossRevenue (the synced,
//     invoiced-frozen figure) at the child's own tier — COs bill independently.
//   - An UNBOOKED CO quote contributes its resolved total (line items first,
//     JM14) as "projected". A lost CO quote is a dead bid, not projected money.
//   - COs chain: a change order on a change order still rolls up to the root
//     parent, so skipped child rows can never strand grandchild money.
export function jobRevenueParts(job, quotes = [], jobs = []) {
  const liveQuotes = (quotes || []).filter((q) => isLiveDoc(q) && !q.draft);
  const liveJobsList = (jobs || []).filter(isLiveDoc);

  // A job's recorded worth. Legacy jobs (parents AND booked CO children — gpt
  // F2) can predate the grossRevenue sync; value them at their linked quote
  // rather than $0 (same principle as JM14). An explicit 0 stays 0.
  //
  // Pro bono (2026-09-11): a pro bono job is worth $0 on every money surface,
  // whatever its grossRevenue says (a job flipped on the card after booking, or
  // a legacy fallback to the quote). Its value given is reported separately by
  // insights.js revenueYoY, never as AR.
  const recordedAmount = (j) => (isProBono(j) ? 0 : j?.grossRevenue == null
    ? resolveQuoteRevenue(liveQuotes.find((q) => q.id === j?.quoteId))
    : Number(j.grossRevenue) || 0);
  const base = { amount: recordedAmount(job), tier: jobTier(job) };

  const changeOrders = [];
  const visited = new Set([job?.id]);
  const collect = (parentId) => {
    if (parentId == null) return;
    // Booked children first — including ones whose quote has since been trashed.
    liveJobsList.forEach((child) => {
      if (child.changeOrderOf !== parentId || visited.has(child.id)) return;
      visited.add(child.id);
      changeOrders.push({
        quoteId: child.quoteId || null,
        jobId: child.id,
        amount: recordedAmount(child),
        tier: jobTier(child),
      });
      collect(child.id);
    });
    // Then open CO quotes. A booked/completed quote is represented by its child
    // job even when the child's quoteId back-link is stale (gpt F3) — counting
    // it here again is the $14k-for-$12k double-count. A quote with a live
    // linked job is likewise counted from the job side only.
    liveQuotes.forEach((q) => {
      if (q.changeOrderOfJobId !== parentId) return;
      if (q.jobStatus === "lost" || q.jobStatus === "booked" || q.jobStatus === "completed") return;
      if (liveJobsList.some((j) => j.quoteId === q.id)) return;
      changeOrders.push({ quoteId: q.id, jobId: null, amount: resolveQuoteRevenue(q), tier: "projected" });
    });
  };
  collect(job?.id);

  const total = changeOrders.reduce((s, co) => s + co.amount, base.amount);
  return { base, changeOrders, total };
}

// Thin scalar wrapper so pre-parts call sites keep working.
export const jobRevenue = (job, quotes = [], jobs = []) => jobRevenueParts(job, quotes, jobs).total;

// Every part of a project as a flat list — for tier bucketing.
export const partsList = (parts) => [parts.base, ...parts.changeOrders];

// The iteration every money surface shares: live jobs, minus rolled-up CO
// children, each with its parts. Summing parts.total over this list counts
// every dollar exactly once — Insights, the Dashboard, and JobsList must all
// derive from it so their totals reconcile.
export function revenueRollup(jobs = [], quotes = []) {
  const live = (jobs || []).filter(isLiveDoc);
  return live
    .filter((j) => !isRolledUpChangeOrderJob(j, live))
    .map((job) => ({ job, parts: jobRevenueParts(job, quotes, live) }));
}
